import { useState, useCallback } from 'react';
import { validateFormField } from '../services/formValidation';
import { Logger } from '../utils/logger';

interface UseFormNavigationProps {
  formData: any;
  newFormData: any;
  onSubmit: () => Promise<void>;
  onFileUpload: (files: any, fileName: string, fieldName: string, requestType: string) => void;
  onSaveDraft?: (isSubmitted?: boolean) => Promise<void>;
}

interface UseFormNavigationReturn {
  activeField: number;
  backStep: number[];
  setActiveField: (field: number) => void;
  setBackStep: (step: number[]) => void;
  validateConditions: () => void;
  handleBack: () => void;
  navigateNext: () => void;
}

/**
 * Custom hook for managing multi-step form navigation with conditional logic
 *
 * Features:
 * - Handles conditional branching based on field values (is/is_not rules)
 * - Manages navigation history for back button functionality
 * - Validates current field before allowing navigation
 * - Handles file upload fields specially
 * - Triggers form submission on last field
 */
export const useFormNavigation = ({
  formData,
  newFormData,
  onSubmit,
  onFileUpload,
  onSaveDraft
}: UseFormNavigationProps): UseFormNavigationReturn => {
  const [activeField, setActiveField] = useState<number>(1);
  const [backStep, setBackStep] = useState<number[]>([1]);

  /**
   * Validates the current field before allowing navigation
   *
   * For group fields:
   * - If parent group field has required=true, ALL nested fields become required
   * - Otherwise, validates each nested field based on its individual required setting
   */
  const validateCurrentField = useCallback((): boolean => {
    if (!formData?.form || !formData.form[activeField - 1]) {
      return true;
    }
    const currentField = formData.form[activeField - 1];
    const mainField = currentField.fields[0];

    // Check if this is a group field
    if (mainField.type === 'group_field' && mainField.groupFields && Array.isArray(mainField.groupFields)) {
      // If the parent group field is required, all nested fields become required
      const parentRequired = mainField.required || false;

      // Validate each nested field in the group
      for (const nestedField of mainField.groupFields) {
        // Create a copy of the nested field with the parent's required setting applied
        const fieldToValidate = {
          ...nestedField,
          // If parent is required, nested field is required (override individual setting)
          required: parentRequired ? true : nestedField.required
        };

        const isValid = validateFormField(fieldToValidate, newFormData);
        if (!isValid) {
          return false; // Stop at first invalid field
        }
      }
      return true; // All nested fields are valid
    } else {
      // Regular field validation
      return validateFormField(mainField, newFormData);
    }
  }, [formData, activeField, newFormData]);

  /**
   * Navigates to the next field (used by payment and other hooks)
   */
  const navigateNext = useCallback(async () => {
    // Save draft before navigating
    if (onSaveDraft) {
      await onSaveDraft(false);
    }

    if (activeField === formData?.form?.length - 1) {
      onSubmit();
    } else {
      setActiveField(activeField + 1);
      setBackStep([...backStep, activeField + 1]);
    }
  }, [activeField, backStep, formData, onSubmit, onSaveDraft]);

  /**
   * Validates conditions and navigates accordingly
   * Handles conditional branching (is/is_not rules) and file uploads
   */
  const validateConditions = useCallback(async () => {
    // First validate the current field
    if (!validateCurrentField()) {
      return;
    }

    if (!formData?.form || activeField > formData.form.length) {
      return;
    }

    // Save draft before proceeding with navigation
    if (onSaveDraft) {
      await onSaveDraft(false);
    }

    const formObject = formData.form[activeField - 1];

    if (activeField < formData.form.length) {
      // Check if there are conditional rules
      if (formObject.conditionRule && formObject.conditionRule.length > 0) {
        let conditionMatched = false;

        for (const condition of formObject.conditionRule) {
          const { if: conditionType, value, then_go_to } = condition;
          const fieldValue = newFormData[formObject.fields[0].fieldName];

          if (!fieldValue) continue;

          // Check "is" condition
          if (
            conditionType === "is" &&
            fieldValue.toLowerCase() === value.toLowerCase()
          ) {
            const newVal = then_go_to.split("_");
            const targetField = parseInt(newVal[1]);
            setActiveField(targetField);
            setBackStep([...backStep, targetField]);
            conditionMatched = true;
            break;
          }
          // Check "is_not" condition
          else if (
            conditionType === "is_not" &&
            fieldValue.toLowerCase() !== value.toLowerCase()
          ) {
            const newVal = then_go_to.split("_");
            const targetField = parseInt(newVal[1]);
            setActiveField(targetField);
            setBackStep([...backStep, targetField]);
            conditionMatched = true;
            break;
          }
        }

        // No condition matched, proceed normally
        if (!conditionMatched) {
          if (activeField === formData.form.length - 1) {
            onSubmit();
          } else {
            setActiveField(activeField + 1);
            setBackStep([...backStep, activeField + 1]);
          }
        }
      } else {
        // No conditional rules, proceed normally
        if (activeField === formData.form.length - 1) {
          // Last field - check if it's a file upload
          if (formData.form[activeField - 1].fields[0].type === "file_upload") {
            onFileUpload(
              formData.form[activeField - 1].fields[0].file,
              formData.form[activeField - 1].question,
              formData.form[activeField - 1].fields[0].fieldName,
              "submit"
            );
          } else {
            onSubmit();
          }
        } else {
          // Not the last field - check if it's a file upload
          if (formData.form[activeField - 1].fields[0].type === "file_upload") {
            Logger.log(
              "File upload field type:",
              formData.form[activeField - 1].fields[0].type
            );
            onFileUpload(
              formData.form[activeField - 1].fields[0].file,
              formData.form[activeField - 1].question,
              formData.form[activeField - 1].fields[0].fieldName,
              "next"
            );
          } else {
            setActiveField(activeField + 1);
            setBackStep([...backStep, activeField + 1]);
          }
        }
      }
    }
  }, [activeField, backStep, formData, newFormData, onSubmit, onFileUpload, onSaveDraft, validateCurrentField]);

  /**
   * Handles back navigation using history
   */
  const handleBack = useCallback(() => {
    if (activeField > 1 && backStep.length >= 2) {
      setActiveField(backStep[backStep.length - 2]);
      const newArray = [...backStep];
      newArray.pop();
      setBackStep(newArray);
    }
  }, [activeField, backStep]);

  return {
    activeField,
    backStep,
    setActiveField,
    setBackStep,
    validateConditions,
    handleBack,
    navigateNext
  };
};
